<script setup>
/* global axios */
// [AstraChat] Box de Crédito no painel da conversa: o atendente preenche os dados do
// cliente, autoriza (quando o produto exige) e roda a simulação multibanco ali no
// ticket, vendo ofertas/propostas. Reusa o MESMO motor (Credit::SimulationOrchestrator)
// que os nós de flow da IA.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import KanbanSelect from 'dashboard/routes/dashboard/kanban/KanbanSelect.vue';

const props = defineProps({
  conversationId: { type: [String, Number], required: true },
});

const { t, te } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban/credit`;

const loading = ref(true);
const products = ref([]);
const proposals = ref([]);
const session = ref(null);
const formData = ref({});
const selectedProduct = ref('');
const simulating = ref(false);
const expanded = ref({});
const authorizing = ref({});
const authLinks = ref({});
// OTP (SMS) por banco
const otpSent = ref({});
const otpCode = ref({});
const otpSending = ref({});
const otpVerifying = ref({});

// ---- digitação de proposta + detalhe (modais) ----
const proposeDialog = ref(null);
const detailDialog = ref(null);
const proposalTarget = ref(null); // { bankKey, bankLabel, offer, index }
const proposalDetail = ref(null); // proposta aberta no detalhe
const proposing = ref(false);
const refreshing = ref(false);
const cancelling = ref(false);
// Motivo da última simulação (ex.: 'sem_bancos_ativos') pra avisar em texto
// quando o produto está ativo mas não tem banco configurado.
const lastReason = ref(null);

const cards = computed(() => session.value?.cards || []);
const okCount = computed(
  () => cards.value.filter(c => c.status === 'success').length
);
const currentProduct = computed(() =>
  products.value.find(p => p.product_key === selectedProduct.value)
);
const formFields = computed(() => currentProduct.value?.form || ['cpf']);

// ---- máscaras (pt-BR) ----
const onlyDigits = value => String(value || '').replace(/\D/g, '');
const maskCpf = value =>
  onlyDigits(value)
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
const maskPhone = value => {
  const d = onlyDigits(value).slice(0, 11);
  if (d.length <= 10)
    return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
};
const maskDate = value =>
  onlyDigits(value)
    .slice(0, 8)
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{2})(\d)/, '$1/$2');

const FIELD_DEFS = {
  cpf: { mask: maskCpf, inputmode: 'numeric', placeholder: '000.000.000-00' },
  name: { mask: null, inputmode: 'text', placeholder: '' },
  birth_date: {
    mask: maskDate,
    inputmode: 'numeric',
    placeholder: 'dd/mm/aaaa',
  },
  phone: { mask: maskPhone, inputmode: 'tel', placeholder: '(00) 00000-0000' },
  email: { mask: null, inputmode: 'email', placeholder: 'email@exemplo.com' },
};
// Gênero é exigido pela V8 (CLT) e vai como select (male/female).
const GENDER_OPTIONS = ['male', 'female'];
const fieldDef = field =>
  FIELD_DEFS[field] || { mask: null, inputmode: 'text' };

// Define o valor de um campo do formulário (usado pelos KanbanSelect).
const setField = (field, value) => {
  formData.value = { ...formData.value, [field]: value };
};
const productOptions = computed(() =>
  products.value.map(p => ({ value: p.product_key, label: p.label }))
);
const genderOptions = computed(() =>
  GENDER_OPTIONS.map(g => ({ value: g, label: t(`CREDIT.box.fields.gender_${g}`) }))
);
const maritalOptions = computed(() =>
  ['single', 'married', 'divorced', 'widowed'].map(v => ({
    value: v,
    label: t(`CREDIT.box.propose.${v}`),
  }))
);
const pixTypeOptions = computed(() =>
  [
    { value: 'cpf', label: t('CREDIT.box.propose.pixCpf') },
    { value: 'phone', label: t('CREDIT.box.propose.pixPhone') },
    { value: 'email', label: t('CREDIT.box.propose.pixEmail') },
    { value: 'random', label: t('CREDIT.box.propose.pixRandom') },
  ]
);
const fieldLabel = field => {
  const key = `CREDIT.box.fields.${field}`;
  return te(key) ? t(key) : field;
};
const onFieldInput = (field, event) => {
  const def = fieldDef(field);
  const value = def.mask ? def.mask(event.target.value) : event.target.value;
  formData.value = { ...formData.value, [field]: value };
};

const canSimulate = computed(() => {
  if (onlyDigits(formData.value.cpf).length < 11) return false;
  if (formFields.value.includes('name') && !formData.value.name?.trim())
    return false;
  return true;
});

// ---- moeda / taxa ----
const brl = value => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};
const rate = value => {
  if (value === null || value === undefined || value === '') return value;
  const n = Number(value);
  return Number.isNaN(n) ? String(value) : n.toLocaleString('pt-BR');
};

const offerValue = offer => Number(offer?.valor ?? 0);
const topOffer = card =>
  (card.offers || [])
    .slice()
    .sort((a, b) => offerValue(b) - offerValue(a))[0] || null;

const bestOffer = computed(() => {
  let best = null;
  cards.value.forEach(card => {
    if (card.status !== 'success') return;
    (card.offers || []).forEach(offer => {
      if (!best || offerValue(offer) > offerValue(best)) {
        best = { ...offer, bank_label: card.bank_label };
      }
    });
  });
  return best;
});

const needsAuth = card =>
  card.status === 'error' && card.tone === 'unauthorized';

// Cross-sell: produto simulado não deu nenhuma oferta e nenhum banco pede
// autorização pendente → sugere os outros produtos ativos pro mesmo cliente.
const noOffers = computed(
  () =>
    cards.value.length > 0 &&
    okCount.value === 0 &&
    !cards.value.some(c => needsAuth(c))
);
// Só sugere no cross-sell produtos que TÊM banco configurado — senão mandaria o
// operador pra outro produto que também não simula.
const otherProducts = computed(() =>
  products.value.filter(
    p => p.product_key !== selectedProduct.value && p.has_bank !== false
  )
);

// Produto selecionado está ativo porém sem nenhum banco configurado.
const currentHasBank = computed(() => currentProduct.value?.has_bank !== false);

// A última simulação não achou banco pro produto (nada a consultar).
const noBanksConfigured = computed(
  () =>
    !!session.value &&
    cards.value.length === 0 &&
    lastReason.value === 'sem_bancos_ativos'
);

const dotClass = card => {
  if (card.status === 'success') return 'bg-n-teal-9';
  if (needsAuth(card)) return 'bg-n-amber-9';
  if (card.status === 'error') return 'bg-n-ruby-9';
  if (card.status === 'loading' || card.status === 'polling')
    return 'bg-n-amber-9';
  return 'bg-n-slate-8';
};

const PROPOSAL_TONE = {
  LIQUIDATED_TO_CUSTOMER: 'bg-n-teal-3 text-n-teal-11',
  APPROVED: 'bg-n-teal-3 text-n-teal-11',
  FORMALIZATION_REQUESTED: 'bg-n-amber-3 text-n-amber-11',
  PENDING: 'bg-n-amber-3 text-n-amber-11',
  ANALYSIS: 'bg-n-amber-3 text-n-amber-11',
  CANCELLED: 'bg-n-ruby-3 text-n-ruby-11',
  DENIED: 'bg-n-ruby-3 text-n-ruby-11',
};
const prettify = value =>
  String(value || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^./, c => c.toUpperCase());
const statusLabel = value => {
  const key = `CREDIT.box.status.${value}`;
  return te(key) ? t(key) : prettify(value);
};
const statusClass = value =>
  PROPOSAL_TONE[value] || 'bg-n-alpha-2 text-n-slate-11';

const toggle = bankKey => {
  expanded.value = { ...expanded.value, [bankKey]: !expanded.value[bankKey] };
};

// Abre os bancos com oferta (ver o dinheiro) e os que pedem autorização (ver a ação).
const seedExpanded = () => {
  const next = {};
  cards.value.forEach(card => {
    next[card.bank_key] = card.status === 'success' || needsAuth(card);
  });
  expanded.value = next;
};
const seedAuth = () => {
  const next = {};
  (session.value?.authorizations || []).forEach(a => {
    if (a.link) next[a.bank_key] = a.link;
  });
  authLinks.value = next;
};

const load = async () => {
  loading.value = true;
  try {
    const [prods, sess, props2] = await Promise.all([
      axios.get(`${base}/products`),
      axios.get(`${base}/sessions`, {
        params: { conversation_id: props.conversationId },
      }),
      axios.get(`${base}/proposals`, {
        params: { conversation_id: props.conversationId },
      }),
    ]);
    products.value = (prods.data.data || []).filter(p => p.active);
    if (products.value.length)
      selectedProduct.value = products.value[0].product_key;
    const sessions = sess.data.data || [];
    if (sessions.length) {
      const detail = await axios.get(`${base}/sessions/${sessions[0].id}`);
      session.value = detail.data.data;
      seedExpanded();
      seedAuth();
    }
    proposals.value = props2.data.data || [];
  } catch {
    // módulo pode estar desligado — o box nem renderiza nesse caso
  } finally {
    loading.value = false;
  }
};

const buildHolder = () => {
  const holder = {};
  formFields.value.forEach(field => {
    if (field !== 'cpf' && formData.value[field])
      holder[field] = formData.value[field];
  });
  return holder;
};

const simular = async () => {
  if (!selectedProduct.value || !canSimulate.value) return;
  simulating.value = true;
  try {
    const created = await axios.post(`${base}/sessions`, {
      product_key: selectedProduct.value,
      cpf: onlyDigits(formData.value.cpf),
      holder: buildHolder(),
      conversation_id: props.conversationId,
    });
    const id = created.data.data.id;
    const result = await axios.post(`${base}/sessions/${id}/simulate`);
    session.value = result.data.data;
    lastReason.value = result.data.result?.reason || null;
    seedExpanded();
    seedAuth();
  } catch {
    useAlert(t('CREDIT.errors.save'));
  } finally {
    simulating.value = false;
  }
};

// Re-roda a simulação na MESMA sessão (após autorizar um banco, por ex.).
const resimular = async () => {
  if (!session.value?.id) return;
  simulating.value = true;
  try {
    const result = await axios.post(
      `${base}/sessions/${session.value.id}/simulate`
    );
    session.value = result.data.data;
    lastReason.value = result.data.result?.reason || null;
    seedExpanded();
    seedAuth();
  } catch {
    useAlert(t('CREDIT.errors.save'));
  } finally {
    simulating.value = false;
  }
};

const authorizeBank = async bankKey => {
  if (!session.value?.id) return;
  authorizing.value = { ...authorizing.value, [bankKey]: true };
  try {
    const r = await axios.post(
      `${base}/sessions/${session.value.id}/banks/${bankKey}/authorize`
    );
    const link = r.data?.data?.link || r.data?.result?.link || true;
    authLinks.value = { ...authLinks.value, [bankKey]: link };
  } catch {
    useAlert(t('CREDIT.errors.save'));
  } finally {
    authorizing.value = { ...authorizing.value, [bankKey]: false };
  }
};

const sendOtp = async bankKey => {
  if (!session.value?.id) return;
  otpSending.value = { ...otpSending.value, [bankKey]: true };
  try {
    await axios.post(
      `${base}/sessions/${session.value.id}/banks/${bankKey}/otp/send`
    );
    otpSent.value = { ...otpSent.value, [bankKey]: true };
  } catch {
    useAlert(t('CREDIT.errors.save'));
  } finally {
    otpSending.value = { ...otpSending.value, [bankKey]: false };
  }
};

const verifyOtp = async bankKey => {
  if (!session.value?.id) return;
  otpVerifying.value = { ...otpVerifying.value, [bankKey]: true };
  try {
    const r = await axios.post(
      `${base}/sessions/${session.value.id}/banks/${bankKey}/otp/verify`,
      { code: otpCode.value[bankKey] }
    );
    if (r.data?.result?.authorized) {
      await resimular();
    } else {
      useAlert(t('CREDIT.box.otpInvalid'));
    }
  } catch {
    useAlert(t('CREDIT.box.otpInvalid'));
  } finally {
    otpVerifying.value = { ...otpVerifying.value, [bankKey]: false };
  }
};

// ---- timeline da proposta (espelha o ciclo do banco) ----
const TIMELINE_STEPS = ['awaitingSignature', 'signed', 'processing', 'paid'];
const STEP_INDEX = {
  FORMALIZATION_REQUESTED: 0,
  PENDING: 2,
  ANALYSIS: 2,
  APPROVED: 2,
  LIQUIDATED_TO_CUSTOMER: 3,
};
const isTerminalNegative = situacao =>
  situacao === 'CANCELLED' || situacao === 'DENIED';
const currentStep = computed(
  () => STEP_INDEX[proposalDetail.value?.situacao] ?? 0
);

// Abre o modal de digitar a oferta como proposta.
const proposalForm = ref({});
const openPropose = (card, offer, index) => {
  proposalTarget.value = {
    bankKey: card.bank_key,
    bankLabel: card.bank_label,
    offer,
    index,
  };
  proposalForm.value = {
    mother_name: '',
    marital_status: 'single',
    document_identification_number: '',
    cep: '',
    street: '',
    number: '',
    neighborhood: '',
    city: '',
    state: '',
    pix_key: '',
    pix_key_type: 'cpf',
  };
  proposeDialog.value?.open();
};

const confirmPropose = async () => {
  const tgt = proposalTarget.value;
  if (!tgt || !session.value?.id) return;
  proposing.value = true;
  try {
    const r = await axios.post(
      `${base}/sessions/${session.value.id}/banks/${tgt.bankKey}/proposal`,
      { offer_index: tgt.index, borrower: proposalForm.value }
    );
    proposals.value = [r.data.data, ...proposals.value];
    proposeDialog.value?.close();
    useAlert(t('CREDIT.box.proposalCreated'));
  } catch {
    useAlert(t('CREDIT.errors.save'));
  } finally {
    proposing.value = false;
  }
};

const openDetail = proposal => {
  proposalDetail.value = proposal;
  detailDialog.value?.open();
};

const applyProposalUpdate = data => {
  proposalDetail.value = data;
  proposals.value = proposals.value.map(p => (p.id === data.id ? data : p));
};

const refreshProposal = async () => {
  const p = proposalDetail.value;
  if (!p) return;
  refreshing.value = true;
  try {
    const r = await axios.post(`${base}/proposals/${p.id}/refresh`);
    applyProposalUpdate(r.data.data);
  } catch {
    useAlert(t('CREDIT.errors.save'));
  } finally {
    refreshing.value = false;
  }
};

const cancelProposal = async () => {
  const p = proposalDetail.value;
  if (!p) return;
  cancelling.value = true;
  try {
    const r = await axios.post(`${base}/proposals/${p.id}/cancel`);
    applyProposalUpdate(r.data.data);
  } catch {
    useAlert(t('CREDIT.errors.save'));
  } finally {
    cancelling.value = false;
  }
};

const copyFormalization = async url => {
  if (!url) return;
  try {
    await navigator.clipboard.writeText(url);
    useAlert(t('CREDIT.box.linkCopied'));
  } catch {
    useAlert(t('CREDIT.errors.save'));
  }
};

// Cross-sell: troca o produto e re-simula reaproveitando os dados já preenchidos.
const crossSell = async productKey => {
  selectedProduct.value = productKey;
  await simular();
};

onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-4 px-3.5 pt-2.5 pb-3.5">
    <div v-if="loading" class="text-xs text-n-slate-11">
      {{ t('CREDIT.loading') }}
    </div>

    <template v-else>
      <p v-if="!products.length" class="text-xs text-n-slate-11">
        {{ t('CREDIT.box.noProducts') }}
      </p>

      <!-- Simulador: produto + campos dinâmicos do cliente -->
      <div v-else class="flex flex-col gap-2.5">
        <KanbanSelect v-model="selectedProduct" :options="productOptions" inline />

        <!-- Produto ativo mas sem banco configurado: avisa antes de simular -->
        <div
          v-if="!currentHasBank"
          class="flex items-start gap-1.5 p-2.5 text-xs border rounded-lg border-n-amber-6 bg-n-amber-2 text-n-amber-11"
        >
          <span class="i-lucide-triangle-alert w-3.5 h-3.5 mt-px shrink-0" />
          <span>{{ t('CREDIT.box.noBankForProduct') }}</span>
        </div>

        <div
          v-for="field in formFields"
          :key="field"
          class="flex flex-col gap-1"
        >
          <label
            class="text-[11px] font-medium text-n-slate-11"
            :for="`credit-${field}`"
          >
            {{ fieldLabel(field) }}
          </label>
          <KanbanSelect
            v-if="field === 'gender'"
            :model-value="formData[field] || ''"
            :options="genderOptions"
            :placeholder="t('CREDIT.box.fields.genderSelect')"
            inline
            @change="setField(field, $event)"
          />
          <input
            v-else
            :id="`credit-${field}`"
            :value="formData[field] || ''"
            :inputmode="fieldDef(field).inputmode"
            :placeholder="fieldDef(field).placeholder"
            class="w-full h-9 px-2.5 text-sm transition-colors border rounded-lg outline-none border-n-weak bg-n-alpha-black1 text-n-slate-12 placeholder:text-n-slate-10 hover:border-n-slate-6 focus:border-n-brand"
            @input="onFieldInput(field, $event)"
            @keyup.enter="simular"
          />
        </div>

        <button
          class="flex items-center justify-center w-full h-9 gap-1.5 text-sm font-medium text-white transition-opacity rounded-lg bg-n-brand hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="simulating || !canSimulate"
          @click="simular"
        >
          <span
            v-if="simulating"
            class="w-3.5 h-3.5 border-2 rounded-full border-white/40 border-t-white animate-spin"
          />
          {{
            simulating ? t('CREDIT.box.simulating') : t('CREDIT.box.simulate')
          }}
        </button>
      </div>

      <!-- Destaque: melhor oferta -->
      <div
        v-if="bestOffer"
        class="flex flex-col gap-1 p-3 border rounded-lg border-n-teal-6 bg-n-teal-2"
      >
        <span
          class="text-[10px] font-semibold tracking-wider uppercase text-n-teal-11"
        >
          {{ t('CREDIT.box.bestOffer') }}
        </span>
        <span
          class="text-xl font-bold leading-tight text-n-slate-12 tabular-nums"
        >
          {{ brl(bestOffer.valor) }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ bestOffer.bank_label }} ·
          {{
            t('CREDIT.box.installmentsOf', {
              n: bestOffer.parcelas,
              value: brl(bestOffer.valor_parcela),
            })
          }}
          <template v-if="bestOffer.taxa_mensal">
            ·
            {{
              t('CREDIT.box.ratePerMonth', {
                rate: rate(bestOffer.taxa_mensal),
              })
            }}
          </template>
        </span>
      </div>

      <!-- Simulou mas o produto não tem banco configurado: mensagem clara -->
      <div
        v-if="noBanksConfigured"
        class="flex flex-col gap-2 p-3 border rounded-lg border-n-amber-6 bg-n-amber-2"
      >
        <span class="flex items-start gap-1.5 text-xs font-medium text-n-amber-11">
          <span class="i-lucide-triangle-alert w-3.5 h-3.5 mt-px shrink-0" />
          {{ t('CREDIT.box.noBankForProduct') }}
        </span>
        <template v-if="otherProducts.length">
          <span class="text-xs text-n-amber-11">
            {{ t('CREDIT.box.tryConfiguredProduct') }}
          </span>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="product in otherProducts"
              :key="product.product_key"
              class="px-3 h-8 text-xs font-medium transition-colors border rounded-lg border-n-amber-7 text-n-amber-11 hover:bg-n-amber-3 disabled:opacity-50"
              :disabled="simulating"
              @click="crossSell(product.product_key)"
            >
              {{ product.label }}
            </button>
          </div>
        </template>
      </div>

      <!-- Cross-sell: sem oferta neste produto, oferece outro -->
      <div
        v-if="noOffers && otherProducts.length"
        class="flex flex-col gap-2 p-3 border rounded-lg border-n-amber-6 bg-n-amber-2"
      >
        <span class="text-xs font-medium text-n-amber-11">
          {{ t('CREDIT.box.crossSellTitle') }}
        </span>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="product in otherProducts"
            :key="product.product_key"
            class="px-3 h-8 text-xs font-medium transition-colors border rounded-lg border-n-amber-7 text-n-amber-11 hover:bg-n-amber-3 disabled:opacity-50"
            :disabled="simulating"
            @click="crossSell(product.product_key)"
          >
            {{ product.label }}
          </button>
        </div>
      </div>

      <!-- Resultado por banco -->
      <div v-if="cards.length" class="flex flex-col gap-2">
        <div class="flex items-baseline justify-between">
          <p
            class="text-[11px] font-semibold tracking-wide uppercase text-n-slate-11"
          >
            {{ t('CREDIT.box.results') }}
          </p>
          <span class="text-[11px] text-n-slate-10 tabular-nums">
            {{
              t('CREDIT.box.withOffers', { ok: okCount, total: cards.length })
            }}
          </span>
        </div>

        <div
          v-for="card in cards"
          :key="card.bank_key"
          class="overflow-hidden border rounded-lg border-n-weak"
        >
          <button
            class="flex items-center justify-between w-full gap-2 px-3 py-2 text-left transition-colors hover:bg-n-alpha-1"
            @click="toggle(card.bank_key)"
          >
            <span class="flex items-center min-w-0 gap-2">
              <span
                class="w-1.5 h-1.5 rounded-full shrink-0"
                :class="dotClass(card)"
              />
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ card.bank_label }}
              </span>
            </span>
            <span class="flex items-center gap-2 shrink-0">
              <span
                v-if="card.status === 'success' && topOffer(card)"
                class="text-sm font-semibold text-n-slate-12 tabular-nums"
              >
                {{ brl(topOffer(card).valor) }}
              </span>
              <span
                v-else-if="needsAuth(card)"
                class="text-[11px] font-medium text-n-amber-11"
              >
                {{ t('CREDIT.box.authorize') }}
              </span>
              <span
                v-else
                class="text-[11px] text-n-slate-10 truncate max-w-32"
              >
                {{ t('CREDIT.box.noOffer') }}
              </span>
              <svg
                class="w-4 h-4 transition-transform text-n-slate-10 shrink-0"
                :class="{ 'rotate-180': expanded[card.bank_key] }"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                stroke-width="1.75"
              >
                <path
                  d="M6 8l4 4 4-4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
          </button>

          <!-- ofertas -->
          <div
            v-if="expanded[card.bank_key] && card.status === 'success'"
            class="border-t divide-y border-n-weak divide-n-weak"
          >
            <div
              v-for="(offer, index) in card.offers"
              :key="index"
              class="flex flex-col gap-2 px-3 py-2 bg-n-alpha-black1"
            >
              <div class="flex items-center justify-between gap-2">
                <div class="flex flex-col min-w-0">
                  <span
                    class="text-sm font-semibold text-n-slate-12 tabular-nums"
                  >
                    {{ brl(offer.valor) }}
                  </span>
                  <span class="text-[11px] text-n-slate-10 tabular-nums">
                    {{
                      t('CREDIT.box.installmentsOf', {
                        n: offer.parcelas,
                        value: brl(offer.valor_parcela),
                      })
                    }}
                  </span>
                </div>
                <div class="flex flex-col items-end shrink-0">
                  <span
                    v-if="offer.taxa_mensal"
                    class="text-xs text-n-slate-11 tabular-nums"
                  >
                    {{
                      t('CREDIT.box.ratePerMonth', {
                        rate: rate(offer.taxa_mensal),
                      })
                    }}
                  </span>
                  <span v-if="offer.tabela" class="text-[11px] text-n-slate-10">
                    {{ offer.tabela }}
                  </span>
                </div>
              </div>
              <button
                class="w-full h-8 text-xs font-medium text-white transition-opacity rounded-lg bg-n-brand hover:opacity-90"
                @click="openPropose(card, offer, index)"
              >
                {{ t('CREDIT.box.digitarProposta') }}
              </button>
            </div>
          </div>

          <!-- autorização pendente -->
          <div
            v-else-if="expanded[card.bank_key] && needsAuth(card)"
            class="flex flex-col gap-2 px-3 py-2.5 border-t border-n-weak bg-n-amber-2"
          >
            <span class="text-xs text-n-amber-11">
              {{ t('CREDIT.box.awaitingAuth') }}
            </span>
            <a
              v-if="
                authLinks[card.bank_key] && authLinks[card.bank_key] !== true
              "
              :href="authLinks[card.bank_key]"
              target="_blank"
              rel="noopener noreferrer"
              class="text-xs font-medium text-n-brand hover:underline"
            >
              {{ t('CREDIT.box.authLink') }} →
            </a>
            <div class="flex gap-2">
              <button
                class="flex items-center justify-center flex-1 h-8 gap-1.5 text-xs font-medium transition-colors border rounded-lg border-n-amber-7 text-n-amber-11 hover:bg-n-amber-3 disabled:opacity-50"
                :disabled="authorizing[card.bank_key]"
                @click="authorizeBank(card.bank_key)"
              >
                {{
                  authorizing[card.bank_key]
                    ? t('CREDIT.box.authorizing')
                    : t('CREDIT.box.authorize')
                }}
              </button>
              <button
                v-if="authLinks[card.bank_key]"
                class="flex-1 h-8 text-xs font-medium text-white transition-opacity rounded-lg bg-n-brand hover:opacity-90 disabled:opacity-50"
                :disabled="simulating"
                @click="resimular"
              >
                {{ t('CREDIT.box.resimulate') }}
              </button>
            </div>

            <!-- OTP por SMS (alternativa ao link) -->
            <div class="flex items-center gap-2 pt-1">
              <span class="flex-1 h-px bg-n-amber-7/40" />
              <span class="text-[10px] uppercase tracking-wide text-n-amber-11">
                {{ t('CREDIT.box.orSms') }}
              </span>
              <span class="flex-1 h-px bg-n-amber-7/40" />
            </div>
            <button
              v-if="!otpSent[card.bank_key]"
              class="flex items-center justify-center w-full h-8 gap-1.5 text-xs font-medium transition-colors border rounded-lg border-n-amber-7 text-n-amber-11 hover:bg-n-amber-3 disabled:opacity-50"
              :disabled="otpSending[card.bank_key]"
              @click="sendOtp(card.bank_key)"
            >
              {{
                otpSending[card.bank_key]
                  ? t('CREDIT.box.otpSending')
                  : t('CREDIT.box.otpSend')
              }}
            </button>
            <div v-else class="flex gap-2">
              <input
                v-model="otpCode[card.bank_key]"
                inputmode="numeric"
                maxlength="6"
                :placeholder="t('CREDIT.box.otpPlaceholder')"
                class="flex-1 h-8 px-2.5 text-xs text-center border rounded-lg outline-none tabular-nums border-n-amber-7 bg-n-alpha-black1 text-n-slate-12 focus:border-n-brand"
              />
              <button
                class="h-8 px-3 text-xs font-medium text-white transition-opacity rounded-lg bg-n-brand hover:opacity-90 disabled:opacity-50"
                :disabled="
                  otpVerifying[card.bank_key] || !otpCode[card.bank_key]
                "
                @click="verifyOtp(card.bank_key)"
              >
                {{
                  otpVerifying[card.bank_key]
                    ? t('CREDIT.box.otpVerifying')
                    : t('CREDIT.box.otpVerify')
                }}
              </button>
            </div>
          </div>

          <!-- outros erros -->
          <div
            v-else-if="expanded[card.bank_key]"
            class="px-3 py-2 text-xs border-t text-n-ruby-11 border-n-weak bg-n-alpha-black1"
          >
            {{ card.error_code || t('CREDIT.box.noOffer') }}
          </div>
        </div>
      </div>

      <!-- Propostas do contato -->
      <div v-if="proposals.length" class="flex flex-col gap-2">
        <p
          class="text-[11px] font-semibold tracking-wide uppercase text-n-slate-11"
        >
          {{ t('CREDIT.box.proposals') }}
        </p>
        <div
          v-for="proposal in proposals"
          :key="proposal.id"
          class="flex flex-col gap-1.5 px-3 py-2 text-left transition-colors border rounded-lg cursor-pointer border-n-weak hover:border-n-slate-6 hover:bg-n-alpha-1"
          role="button"
          tabindex="0"
          @click="openDetail(proposal)"
          @keydown.enter="openDetail(proposal)"
        >
          <div class="flex items-center justify-between gap-2">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ proposal.bank_label }}
            </span>
            <span
              class="px-2 py-0.5 text-[11px] font-medium rounded-full shrink-0"
              :class="statusClass(proposal.situacao)"
            >
              {{ statusLabel(proposal.situacao) }}
            </span>
          </div>
          <div class="flex items-center justify-between gap-2">
            <span class="text-xs text-n-slate-11 tabular-nums">
              <template v-if="proposal.valor">{{
                brl(proposal.valor)
              }}</template>
              <template v-if="proposal.parcelas">
                · {{ proposal.parcelas }}x</template
              >
              <template v-if="proposal.numero_proposta">
                ·
                {{
                  t('CREDIT.box.proposalNumber', {
                    n: proposal.numero_proposta,
                  })
                }}</template
              >
            </span>
            <a
              v-if="proposal.formalizacao_url"
              :href="proposal.formalizacao_url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-xs font-medium whitespace-nowrap text-n-brand hover:underline shrink-0"
              @click.stop
            >
              {{ t('CREDIT.box.formalize') }} →
            </a>
          </div>
        </div>
      </div>
    </template>

    <!-- Modal: digitar oferta como proposta -->
    <Dialog
      ref="proposeDialog"
      :title="t('CREDIT.box.proposeTitle')"
      :confirm-button-label="t('CREDIT.box.digitarProposta')"
      :is-loading="proposing"
      width="sm"
      @confirm="confirmPropose"
    >
      <div v-if="proposalTarget" class="flex flex-col gap-3">
        <p class="text-sm text-n-slate-11">
          {{ t('CREDIT.box.proposeHint', { bank: proposalTarget.bankLabel }) }}
        </p>
        <div
          class="flex flex-col gap-1.5 p-3 border rounded-lg border-n-weak bg-n-alpha-black1"
        >
          <span class="text-base font-semibold text-n-slate-12 tabular-nums">
            {{ brl(proposalTarget.offer.valor) }}
          </span>
          <span class="text-xs text-n-slate-11 tabular-nums">
            {{
              t('CREDIT.box.installmentsOf', {
                n: proposalTarget.offer.parcelas,
                value: brl(proposalTarget.offer.valor_parcela),
              })
            }}
            <template v-if="proposalTarget.offer.taxa_mensal">
              ·
              {{
                t('CREDIT.box.ratePerMonth', {
                  rate: rate(proposalTarget.offer.taxa_mensal),
                })
              }}
            </template>
          </span>
        </div>

        <!-- Dados do tomador exigidos pela digitação (V8/custom) -->
        <p class="text-[11px] font-semibold tracking-wide uppercase text-n-slate-11 mt-1">
          {{ t('CREDIT.box.propose.dataTitle') }}
        </p>
        <div class="grid grid-cols-2 gap-2">
          <input
            v-model="proposalForm.mother_name"
            :placeholder="t('CREDIT.box.propose.motherName')"
            class="col-span-2 h-8 px-2 text-xs border rounded-md border-n-weak bg-n-alpha-black1 text-n-slate-12"
          />
          <KanbanSelect
            v-model="proposalForm.marital_status"
            :options="maritalOptions"
            inline
          />
          <input
            v-model="proposalForm.document_identification_number"
            :placeholder="t('CREDIT.box.propose.rg')"
            class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-alpha-black1 text-n-slate-12"
          />
          <input
            v-model="proposalForm.cep"
            :placeholder="t('CREDIT.box.propose.cep')"
            class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-alpha-black1 text-n-slate-12"
          />
          <input
            v-model="proposalForm.number"
            :placeholder="t('CREDIT.box.propose.number')"
            class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-alpha-black1 text-n-slate-12"
          />
          <input
            v-model="proposalForm.street"
            :placeholder="t('CREDIT.box.propose.street')"
            class="col-span-2 h-8 px-2 text-xs border rounded-md border-n-weak bg-n-alpha-black1 text-n-slate-12"
          />
          <input
            v-model="proposalForm.neighborhood"
            :placeholder="t('CREDIT.box.propose.neighborhood')"
            class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-alpha-black1 text-n-slate-12"
          />
          <input
            v-model="proposalForm.city"
            :placeholder="t('CREDIT.box.propose.city')"
            class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-alpha-black1 text-n-slate-12"
          />
          <input
            v-model="proposalForm.state"
            maxlength="2"
            :placeholder="t('CREDIT.box.propose.state')"
            class="h-8 px-2 text-xs uppercase border rounded-md border-n-weak bg-n-alpha-black1 text-n-slate-12"
          />
          <input
            v-model="proposalForm.pix_key"
            :placeholder="t('CREDIT.box.propose.pixKey')"
            class="h-8 px-2 text-xs border rounded-md border-n-weak bg-n-alpha-black1 text-n-slate-12"
          />
          <div class="col-span-2">
            <KanbanSelect
              v-model="proposalForm.pix_key_type"
              :options="pixTypeOptions"
              inline
            />
          </div>
        </div>
      </div>
    </Dialog>

    <!-- Modal: detalhe da proposta (timeline + ações) -->
    <Dialog
      ref="detailDialog"
      :title="t('CREDIT.box.detailTitle')"
      width="md"
      :show-confirm-button="false"
      :cancel-button-label="t('CREDIT.box.close')"
    >
      <div v-if="proposalDetail" class="flex flex-col gap-4">
        <div class="flex items-center justify-between gap-2">
          <div class="flex flex-col min-w-0">
            <span class="text-sm font-semibold text-n-slate-12">
              {{ proposalDetail.bank_label }}
            </span>
            <span
              v-if="proposalDetail.numero_proposta"
              class="text-[11px] text-n-slate-10 tabular-nums"
            >
              {{
                t('CREDIT.box.proposalNumber', {
                  n: proposalDetail.numero_proposta,
                })
              }}
            </span>
          </div>
          <span
            class="px-2 py-0.5 text-[11px] font-medium rounded-full shrink-0"
            :class="statusClass(proposalDetail.situacao)"
          >
            {{ statusLabel(proposalDetail.situacao) }}
          </span>
        </div>

        <div class="grid grid-cols-3 gap-2">
          <div class="flex flex-col">
            <span class="text-[11px] text-n-slate-10">
              {{ t('CREDIT.box.value') }}
            </span>
            <span class="text-sm font-semibold text-n-slate-12 tabular-nums">
              {{ brl(proposalDetail.valor) }}
            </span>
          </div>
          <div class="flex flex-col">
            <span class="text-[11px] text-n-slate-10">
              {{ t('CREDIT.box.installments') }}
            </span>
            <span class="text-sm font-semibold text-n-slate-12 tabular-nums">
              {{ proposalDetail.parcelas || '—' }}
            </span>
          </div>
          <div class="flex flex-col">
            <span class="text-[11px] text-n-slate-10">
              {{ t('CREDIT.box.rate') }}
            </span>
            <span class="text-sm font-semibold text-n-slate-12 tabular-nums">
              <template v-if="proposalDetail.taxa_mensal">
                {{
                  t('CREDIT.box.ratePerMonth', {
                    rate: rate(proposalDetail.taxa_mensal),
                  })
                }}
              </template>
              <template v-else>—</template>
            </span>
          </div>
        </div>

        <div
          v-if="!isTerminalNegative(proposalDetail.situacao)"
          class="flex flex-col gap-2"
        >
          <span
            class="text-[11px] font-semibold tracking-wide uppercase text-n-slate-11"
          >
            {{ t('CREDIT.box.timeline.title') }}
          </span>
          <ol class="flex flex-col">
            <li
              v-for="(step, i) in TIMELINE_STEPS"
              :key="step"
              class="flex items-start gap-2.5"
            >
              <span class="flex flex-col items-center self-stretch">
                <span
                  class="flex items-center justify-center w-4 h-4 border rounded-full shrink-0"
                  :class="
                    i <= currentStep
                      ? 'bg-n-teal-9 border-n-teal-9'
                      : 'border-n-slate-6'
                  "
                >
                  <span
                    v-if="i <= currentStep"
                    class="w-3 h-3 text-white i-lucide-check"
                  />
                </span>
                <span
                  v-if="i < TIMELINE_STEPS.length - 1"
                  class="flex-1 w-px min-h-4"
                  :class="i < currentStep ? 'bg-n-teal-9' : 'bg-n-slate-6'"
                />
              </span>
              <span
                class="pb-3 text-sm"
                :class="
                  i <= currentStep ? 'text-n-slate-12' : 'text-n-slate-10'
                "
              >
                {{ t(`CREDIT.box.timeline.${step}`) }}
              </span>
            </li>
          </ol>
        </div>

        <div class="flex flex-col gap-2">
          <a
            v-if="proposalDetail.formalizacao_url"
            :href="proposalDetail.formalizacao_url"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center justify-center w-full h-9 text-sm font-medium text-white rounded-lg bg-n-brand hover:opacity-90"
          >
            {{ t('CREDIT.box.formalize') }} →
          </a>
          <div class="flex gap-2">
            <button
              v-if="proposalDetail.formalizacao_url"
              type="button"
              class="flex items-center justify-center flex-1 h-9 gap-1.5 text-sm font-medium transition-colors border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-alpha-1"
              @click="copyFormalization(proposalDetail.formalizacao_url)"
            >
              <span class="w-4 h-4 i-lucide-copy" />
              {{ t('CREDIT.box.copyLink') }}
            </button>
            <button
              type="button"
              class="flex items-center justify-center flex-1 h-9 gap-1.5 text-sm font-medium transition-colors border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-alpha-1 disabled:opacity-50"
              :disabled="refreshing"
              @click="refreshProposal"
            >
              <span
                class="w-4 h-4 i-lucide-refresh-cw"
                :class="{ 'animate-spin': refreshing }"
              />
              {{ t('CREDIT.box.refreshStatus') }}
            </button>
          </div>
          <button
            v-if="!isTerminalNegative(proposalDetail.situacao)"
            type="button"
            class="flex items-center justify-center w-full h-9 text-sm font-medium transition-colors border rounded-lg border-n-ruby-3 text-n-ruby-11 hover:bg-n-ruby-3 disabled:opacity-50"
            :disabled="cancelling"
            @click="cancelProposal"
          >
            {{ t('CREDIT.box.cancelProposal') }}
          </button>
        </div>
      </div>
    </Dialog>
  </div>
</template>
